import { useEffect, useRef, useState } from "react";

/**
 * 스크롤 방향을 감지한다.
 * - 아래로 스크롤: "down"
 * - 위로 스크롤: "up"
 *
 * 이 앱은 window 가 아니라 PullToRefresh 의 `.ptr-scroll-container` 안에서
 * 스크롤이 일어나므로, 해당 컨테이너를 우선 대상으로 삼고 없으면 window 로 폴백한다.
 *
 * @param {object} options
 * @param {number} options.threshold 방향 전환으로 인정할 최소 이동량(px)
 * @param {number} options.topOffset 이 값 이하 영역에서는 항상 "up" 으로 고정
 * @param {string} options.targetSelector 스크롤 대상 셀렉터
 * @param {any} options.resetKey 값이 바뀌면 대상을 다시 찾고 상태를 초기화(라우트 변경 등)
 * @returns {"up" | "down"}
 */
function useScrollDirection({
    threshold = 8,
    topOffset = 60,
    targetSelector = ".ptr-scroll-container",
    resetKey,
} = {}) {
    const [direction, setDirection] = useState("up");
    const lastScrollY = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        const target = document.querySelector(targetSelector) || window;
        const getScrollTop = () =>
            target === window ? window.scrollY : target.scrollTop;

        // 라우트가 바뀌면 컨테이너가 최상단(scrollTop=0)으로 리셋되므로 항상 노출부터 시작한다.
        lastScrollY.current = getScrollTop();
        setDirection("up");

        const update = () => {
            const currentY = Math.max(getScrollTop(), 0);
            const diff = currentY - lastScrollY.current;

            // 최상단 근처, 그리고 미세한 흔들림(바운스)은 방향 전환으로 보지 않는다.
            if (currentY <= topOffset) {
                setDirection("up");
                lastScrollY.current = currentY;
            } else if (Math.abs(diff) >= threshold) {
                setDirection(diff > 0 ? "down" : "up");
                lastScrollY.current = currentY;
            }

            ticking.current = false;
        };

        const handleScroll = () => {
            if (ticking.current) return;
            ticking.current = true;
            window.requestAnimationFrame(update);
        };

        target.addEventListener("scroll", handleScroll, { passive: true });
        return () => target.removeEventListener("scroll", handleScroll);
    }, [threshold, topOffset, targetSelector, resetKey]);

    return direction;
}

export default useScrollDirection;
